import React, { useContext, useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { SettingsContext } from "../../../contexts";
import { useUpsertQuote } from "../../../order/hooks/quotes";
import { Visible } from "../../../core/components";
import { FieldGroup } from "../../../components";
import FormikAutoSave from "./FormikAutoSave";
import { Badge, Button } from "../../../components/ui";
import { isMulticurrency, getBaseCurrency } from "../../../core/currency";

const afterFee = (fee, price) => {
  return price - price * (fee / 100);
};

const validationSchema = Yup.object().shape({
  fee: Yup.number().typeError("Invalid"),
  min_ch24: Yup.number().typeError("Invalid"),
  max_ch24: Yup.number().typeError("Invalid"),
  min_dao: Yup.number().typeError("Invalid"),
  max_dao: Yup.number().typeError("Invalid"),
  min_trade: Yup.number().typeError("Invalid"),
  max_trade: Yup.number().typeError("Invalid"),
  consignment_fee: Yup.number().typeError("Invalid"),
});

const priceFields = [
  "min_ch24",
  "max_ch24",
  "min_ch24_after_fee",
  "max_ch24_after_fee",
  "min_dao",
  "max_dao",
  "min_trade",
  "max_trade",
];

/**
 * Reports the consignment min/max back up whenever the three figures it is
 * derived from change.
 *
 * A component rather than a useEffect inside Formik's render prop: that prop is
 * a child FUNCTION, so a hook written there is registered against Formik, and
 * only stays correct while Formik calls it exactly once per render. Rendering
 * nothing, it exists purely to own the effect.
 */
const ConsignPriceSync = ({ minAfterFee, maxAfterFee, consignmentFee, onChange }) => {
  useEffect(() => {
    if (minAfterFee && maxAfterFee && consignmentFee) {
      onChange({
        min: (minAfterFee - consignmentFee).toFixed(2),
        max: (maxAfterFee - consignmentFee).toFixed(2),
      });
    }
  }, [minAfterFee, maxAfterFee, consignmentFee, onChange]);

  return null;
};

// One cell of the workspace's consignment table: the field without a visible
// label (the row and column heads are its label), named for screen readers.
const Cell = ({ name, label, value, readOnly }) => (
  <div className="qws-field" aria-label={label}>
    <FieldGroup name={name} type="numberFormat" value={value} readOnly={readOnly} placeholder="—" />
  </div>
);

// autoSave: the quote workspace (new setup) — saves on change, no Save button.
// layout="grid": the workspace's compact table; anything else is the modal's.
export function PricingForm({ id, quote, setConsignPrices, quoteSubmitRef, autoSave = false, layout = "legacy" }) {
  const { isShopify, settings } = useContext(SettingsContext);
  const { upsertQuote } = useUpsertQuote();
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // These recommended prices + consignment fee are the OFFER the store makes;
  // they're editable and feed the quote's min_price/max_price, which are stored
  // and paid in the QUOTE'S currency (the CH24 numbers are only a prefill the
  // rep can change). So tag the money sections with the quote currency, not GBP
  // — the value passes through as entered (no conversion). Empty (byte-identical)
  // while multi-currency is off. Read at render time (seedCurrency runs post-import).
  const quoteCurrencyTag = isMulticurrency()
    ? ` (${String(quote?.currency || getBaseCurrency()).toUpperCase()})`
    : "";

  const initialValues = useMemo(
    () =>
      Object.keys(quote.pricing || {}).reduce(
        (pd, key) => ({
          ...pd,
          [key]: priceFields.includes(key)
            ? quote.pricing[key] / 100
            : quote.pricing[key],
        }),
        {}
      ),
    [quote.pricing]
  );

  const handleSubmit = async (data) => {
    setError(null);

    const pricingData = Object.keys(data).reduce(
      (md, key) => ({
        ...md,
        [key]: priceFields.includes(key)
          ? Math.round(parseFloat(data[key]) * 100)
          : parseFloat(data[key]),
      }),
      {}
    );

    try {
      await upsertQuote({
        id,
        quoteData: {
          pricing: pricingData,
          product_id: quote.product_id,
          user_id: quote.user_id,
          currency: quote.currency,
          price: quote.price,
          date: quote.date,
          min_price: Math.round((data.min_ch24_after_fee - data.consignment_fee) * 100),
          max_price: Math.round((data.max_ch24_after_fee - data.consignment_fee) * 100),
        },
      });
      if (quoteSubmitRef.current) {
        quoteSubmitRef.current.click();
      } else {
        console.warn("quoteSubmitRef is null!");
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setError(
        error?.response?.data?.errors?.[0]?.[0] ||
          "The quote pricing could not be saved"
      );
    }
  };

  return (
    <div className="quote-form">
      <Formik
        {...{
          initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        {({
          values: {
            fee,
            min_ch24,
            max_ch24,
            min_ch24_after_fee,
            max_ch24_after_fee,
            min_dao,
            max_dao,
            min_trade,
            max_trade,
            consignment_fee,
          },
        }) => {
          if (layout === "grid") {
            const store = isShopify ? "Selling" : settings?.acronym || "Store";
            const rows = [
              ["Chrono24", "min_ch24", "max_ch24", true],
              [store, "min_dao", "max_dao", false],
              ["Trade", "min_trade", "max_trade", false],
            ];
            return (
              <Form className="qws-consign">
                <ConsignPriceSync
                  minAfterFee={min_ch24_after_fee}
                  maxAfterFee={max_ch24_after_fee}
                  consignmentFee={consignment_fee}
                  onChange={setConsignPrices}
                />
                <div className="qws-form-grid">
                  <div className="qws-field">
                    <FieldGroup name="fee" type="numberFormat" label="Fee %" placeholder="0" />
                  </div>
                  <div className="qws-field">
                    <FieldGroup name="consignment_fee" type="numberFormat" label={`Consignment fee${quoteCurrencyTag}`} placeholder="0" />
                  </div>
                </div>
                <div className="qws-consign__table" role="group" aria-label={`Recommended prices${quoteCurrencyTag}`}>
                  <span />
                  <span className="qws-consign__head">Min</span>
                  <span className="qws-consign__head">Max</span>
                  <span className="qws-consign__head">After fee min</span>
                  <span className="qws-consign__head">After fee max</span>
                  {rows.map(([label, min, max, withFee]) => (
                    <React.Fragment key={min}>
                      <span className="qws-consign__row">{label}{quoteCurrencyTag}</span>
                      <Cell name={min} label={`${label} min`} />
                      <Cell name={max} label={`${label} max`} />
                      {withFee ? (
                        <>
                          <Cell name="min_ch24_after_fee" label={`${label} after fee min`} value={afterFee(fee, min_ch24)} readOnly />
                          <Cell name="max_ch24_after_fee" label={`${label} after fee max`} value={afterFee(fee, max_ch24)} readOnly />
                        </>
                      ) : (
                        <>
                          <span />
                          <span />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <Visible when={error}>
                  <div className="error">{error}</div>
                </Visible>
                {autoSave && <FormikAutoSave />}
              </Form>
            );
          }
          return (
            <Form>
              <ConsignPriceSync
                minAfterFee={min_ch24_after_fee}
                maxAfterFee={max_ch24_after_fee}
                consignmentFee={consignment_fee}
                onChange={setConsignPrices}
              />
              <div className="row">
                <div className="col-12 col-sm-3"></div>

                <div className="col-12 col-sm-3">
                  <FieldGroup
                    name="fee"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "80px",
                      border: !fee ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Fee %:"
                    className={!fee ? "empty-field" : ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-3">
                  <div className="form-subsection-title">
                    Recommended CH24 Price{quoteCurrencyTag}:
                  </div>
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="min_ch24"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !min_ch24 ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Min:"
                    className={!min_ch24 ? "empty-field" : ""}
                  />
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="max_ch24"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !max_ch24 ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Max:"
                    className={!max_ch24 ? "empty-field" : ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-3">
                  <div className="form-subsection-title">After Fee{quoteCurrencyTag}:</div>
                </div>

                <div className="col-6 col-sm-3 readonly">
                  <FieldGroup
                    name="min_ch24_after_fee"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "60px" }}
                    placeholder="Min:"
                    value={afterFee(fee, min_ch24)}
                    readOnly
                  />
                </div>

                <div className="col-6 col-sm-3 readonly">
                  <FieldGroup
                    name="max_ch24_after_fee"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{ paddingLeft: "60px" }}
                    placeholder="Max:"
                    value={afterFee(fee, max_ch24)}
                    readOnly
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-3">
                  <div className="form-subsection-title">
                    Recommended {isShopify ? "Selling" : (settings?.acronym || "Store")} Price{quoteCurrencyTag}:
                  </div>
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="min_dao"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !min_dao ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Min:"
                    className={!min_dao ? "empty-field" : ""}
                  />
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="max_dao"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !max_dao ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Max:"
                    className={!max_dao ? "empty-field" : ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-3">
                  <div className="form-subsection-title">
                    Recommended Trade Price{quoteCurrencyTag}:
                  </div>
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="min_trade"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !min_trade ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Min:"
                    className={!min_trade ? "empty-field" : ""}
                  />
                </div>

                <div className="col-6 col-sm-3">
                  <FieldGroup
                    name="max_trade"
                    type="numberFormat"
                    persistLabel
                    inputStyle={{
                      paddingLeft: "60px",
                      border: !max_trade ? "2px solid #FF8500" : undefined,
                    }}
                    placeholder="Max:"
                    className={!max_trade ? "empty-field" : ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-3">
                  <div className="form-subsection-title">Consignment Fee{quoteCurrencyTag}:</div>
                </div>

                <div className="col-12 col-sm-3">
                  <FieldGroup
                    name="consignment_fee"
                    type="numberFormat"
                    inputStyle={{
                      border: !consignment_fee
                        ? "2px solid #FF8500"
                        : undefined,
                    }}
                    className={!consignment_fee ? "empty-field" : ""}
                  />
                </div>
              </div>
              <Visible when={error}>
                <div className="error">{error}</div>
              </Visible>

              {autoSave ? (
                <FormikAutoSave />
              ) : (
              <div className="row">
                <div className="col-12 col-sm-3 p-1"></div>
                <div className="col-12 col-sm-3 p-1">
                  {showSuccess && (
                    <Badge tone="success">Saved</Badge>
                  )}
                </div>
                <div className="col-12 col-sm-3 p-1">
                  <Button variant="primary" size="lg" fullWidth type="submit" data-btn="quote-pricing-save">
                    Save
                  </Button>
                </div>
              </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
